import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import Colors from '@/Colors';
import DatadogApi from '@/common/apis/DatadogApi';
import { AudienceRuleModes } from '@/common/enums/audience-rule-modes';
import AudienceLogicState from '@/common/enums/AudienceLogicState.enum';
import AudienceRule, { IAudienceRuleAPI } from '@/common/models/audience/AudienceRule';
import { DASH, uniqBy } from '@/globals';
import { useDataMarketplaces, useDataProviders } from '@/org-app/services/tanstack/queries';
import { peekQueryInScope } from '@/org-app/services/tanstack/queryUtils';
import AudienceSegmentUtils from '@/utils/AudienceSegmentUtils';
import { getDiff } from '@/utils/Diff';
import Validatable from '@/validator/Validatable';
import i18n from '@/vueI18n';

export interface IAudienceLogicAPI {
  dataprovider_id: number;
  state: string;
  audiencerules: IAudienceRuleAPI[];
  externaldemographic_ids: number[];
  logical_expression: string;
  demographic_id: number;
  name: string;
  audiencelogic_id: number;
  timestamp: string;
  modified_on_utc: string;
  created_on_utc: string;
}

export default class AudienceLogic extends Validatable {
  constructor(
    public id: number = -1,
    public name: string,
    public demographicID: number,
    public logicalExpression: string = '',
    public audienceRules: AudienceRule[] = [],
    public externalDemographicIDs: number[] = [],
    public timestamp: DateTime = DateTime.local(),
    public state: string,
    public dataProviderID = null,
    public dataMarketplaceID = null,
    public createdOnUtc = null,
    public modifiedOnUtc = null,
    public apiObject = null,
  ) {
    super();
  }

  static fromObject(obj) {
    return super.fromObject(
      obj,
      new AudienceLogic(
        obj.id,
        obj.name,
        obj.demographicID,
        obj.logicalExpression,
        obj.audienceRules,
        obj.externalDemographicIDs,
        obj.timestamp,
        obj.state,
        obj.dataProviderID,
        null,
        obj.createdOnUtc,
        obj.modifiedOnUtc,
        obj.apiObject,
      ),
    );
  }

  isPristine(): boolean {
    if (this.isNew) return true;
    const ignoreProperties = [
      'audience_rules_dict',
      'archived',
      'audiencerules',
      'created_by',
      'created_on_utc',
      'modified_by',
      'modified_on_utc',
      'state',
      'timestamp',
    ];
    const diffs = getDiff(this.apiObject, this.toApi(), ignoreProperties);
    if (diffs.length === 0) return true;
    DatadogApi.logUnexpectedChange(diffs);
    return false;
  }

  get dataProvider() {
    const dataProviders = peekQueryInScope(useDataProviders);
    return dataProviders.find((dp) => dp.id === this.dataProviderID);
  }

  get dataMarketplace() {
    const dataMarketplaces = peekQueryInScope(useDataMarketplaces);
    if (!this.dataMarketplaceID && this.dataProvider?.dataMarketplaceID) {
      this.dataMarketplaceID = this.dataProvider.dataMarketplaceID;
    }
    if (this.dataMarketplaceID) {
      return dataMarketplaces.find((dm) => dm.id === this.dataMarketplaceID);
    } else {
      return null;
    }
  }

  static fromApi(apiObject: IAudienceLogicAPI): AudienceLogic {
    if (apiObject.timestamp == null) {
      apiObject.timestamp = DateTime.local().toISODate();
    }

    const audienceLogic = AudienceLogic.fromObject({
      id: apiObject.audiencelogic_id,
      name: apiObject.name,
      demographicID: apiObject.demographic_id,
      logicalExpression: apiObject.logical_expression,
      externalDemographicIDs: apiObject.externaldemographic_ids,
      audienceRules:
        apiObject.audiencerules !== undefined && apiObject.audiencerules !== null
          ? apiObject.audiencerules.map((x) => AudienceRule.fromApi(x))
          : [],
      apiObject,
      timestamp: DateTime.fromISO(apiObject.timestamp),
      state: apiObject.state,
      dataProviderID: apiObject.dataprovider_id,
      createdOnUtc:
        apiObject.created_on_utc != null ? DateTime.fromISO(apiObject.created_on_utc, { zone: 'utc' }) : null,
      modifiedOnUtc:
        apiObject.modified_on_utc != null ? DateTime.fromISO(apiObject.modified_on_utc, { zone: 'utc' }) : null,
    });
    audienceLogic.apiObject = apiObject;
    return audienceLogic;
  }

  toApi() {
    const originalAudienceRules = this.originalObject !== null ? this.originalObject.audienceRules : null;
    const toAdd = this.audienceRules.filter((x) => x.isNew).map((x) => x.toApi());
    const toUpdate =
      originalAudienceRules !== null
        ? this.audienceRules.filter(
            (x) => originalAudienceRules.filter((y) => !x.isNew && x.timestamp !== null && y.id === x.id).length > 0,
          )
        : [];
    const toDelete =
      originalAudienceRules !== null
        ? originalAudienceRules.filter((x) => this.audienceRules.filter((y) => y.id === x.id).length === 0)
        : [];

    // if there is the same key in delete and in add, update the rule instead
    const shouldUpdateKeys = toAdd
      .filter((value) => toDelete.map((d) => d.id).includes(value.audiencelogic_id))
      .map((v) => v.audiencelogic_id);

    const toUpdateAsDict = {};
    toUpdate.forEach((x) => {
      toUpdateAsDict[x.id] = x.toApi();
    });

    shouldUpdateKeys.forEach((key) => {
      const oldRule = toDelete.find((d) => d.id === key);
      const newRule = AudienceRule.fromApi(toAdd.find((a) => a.audiencelogic_id === key));
      const rule = AudienceRule.fromObject({
        ...newRule,
        id: oldRule.id,
        audienceLookbackWindows: newRule?.audienceLookbackWindows || oldRule.audienceLookbackWindows,
        key,
      });
      toUpdateAsDict[rule.id] = rule.toApi();
    });

    const apiModel = {
      audiencelogic_id: this.id >= 0 ? this.id : undefined,
      name: this.name,
      demographic_id: this.demographicID,
      logical_expression: this.logicalExpression,
      externaldemographic_ids: this.externalDemographicIDs,
      dataprovider_id: this.dataProviderID,
      audience_rules_dict: {
        to_add: toAdd.filter((v) => !shouldUpdateKeys.includes(v.key)),
        to_update: toUpdateAsDict,
        to_delete: toDelete.filter((v) => !shouldUpdateKeys.includes(v.key)),
      },
    };

    if (this.externalDemographicIDs == null) delete apiModel.externaldemographic_ids;

    return apiModel;
  }

  get originalObject(): AudienceLogic | null {
    if (this.apiObject !== null) {
      return AudienceLogic.fromApi(this.apiObject);
    }
    return null;
  }

  private get frontendLogicalExpression() {
    const simpleExpression = AudienceLogic.simplifyExpression(this.logicalExpression);
    const logicalExprRules: AudienceRule[] = simpleExpression
      .split(' ')
      .map((x) => {
        if (Number.isNaN(parseInt(x, 10))) return undefined;
        return this.audienceRules.find((r) => `${r.key}`.localeCompare(x) === 0);
      })
      .filter((r) => r !== undefined);
    // Dict of predefined demo rules, true if rule already mapped, false otherwise
    const predefinedDemoRules: Record<string, boolean> = uniqBy(
      logicalExprRules.filter((r) => r.mode === AudienceRuleModes.PredefinedDemographic),
      'name',
    ).reduce((dict, rule) => {
      dict[rule.name] = false;
      return dict;
    }, {});
    const fixedRules = logicalExprRules.filter((rule) => {
      if (rule.mode != AudienceRuleModes.PredefinedDemographic) return true;
      else if (!predefinedDemoRules[rule.name]) {
        predefinedDemoRules[rule.name] = true;
        return true;
      } else return false;
    });
    return fixedRules.length !== this.audienceRules.length
      ? AudienceLogic.simplifyExpression(AudienceSegmentUtils.rewriteLogicalExpression(this, fixedRules))
      : simpleExpression;
  }

  get stateDescription(): string {
    switch (this.state) {
      case AudienceLogicState.Unsubmitted:
        return capitalize(i18n.global.t('states.audienceLogicUnsubmitted'));
      case AudienceLogicState.Submitted:
        return capitalize(i18n.global.t('states.audienceLogicSubmitted'));
      case AudienceLogicState.Completed:
        return capitalize(i18n.global.t('states.audienceLogicCompleted'));
      default:
        return DASH;
    }
  }

  get statusColor(): string | null {
    switch (this.state) {
      case AudienceLogicState.Completed:
        return Colors.success;
      case AudienceLogicState.Unsubmitted:
        return Colors.error;
      case AudienceLogicState.Submitted:
        return Colors.info;
      default:
        return Colors.error;
    }
  }

  get userFriendlyLogicalExpression(): string {
    return this.frontendLogicalExpression
      .split(' ')
      .map((x) => {
        if (Number.isNaN(parseInt(x, 10))) {
          return x;
        }
        return `${this.audienceRules.find((r) => `${r.key}`.localeCompare(x) === 0) !== undefined ? this.audienceRules.find((r) => `${r.key}`.localeCompare(x) === 0).name : x}`;
      })
      .join(' ');
  }

  get userFriendlyLogicalExpressionHtml(): string {
    return this.frontendLogicalExpression
      .split(' ')
      .map((x) => {
        if (Number.isNaN(parseInt(x, 10))) {
          return `<span>${x}</span>`;
        }
        // PredefinedDemographics rules should be combined in one when displaying in the frontend
        const rule = this.audienceRules.find((r) => `${r.key}`.localeCompare(x) === 0);
        if (rule !== undefined) {
          return `<span class="rule">${rule.name}</span>`;
        } else {
          return `<span class="rule">${x}</span>`;
        }
      })
      .join(' ');
  }

  get userFriendlyBackendLogicalExpression(): string {
    const { logicalExpression, audienceRules } = AudienceSegmentUtils.audienceLogicFeToBe(this);

    const keysInLogicalExpression = logicalExpression.split(/[(,)&|!]+/).filter((c) => c !== '');
    const keysNames = keysInLogicalExpression.map((k) => {
      const rule = audienceRules.find((r) => r.key.toString() === k);
      const name = AudienceSegmentUtils.getSegmentNameForDemoRule(rule, audienceRules);
      return { key: k, name };
    });

    return `${logicalExpression}`
      .replace(/&/g, ' AND ')
      .replace(/\|/g, ' OR ')
      .replace(/!/g, ' NOT ')
      .replace(/\(/g, ' ( ')
      .replace(/\)/g, ' ) ')
      .replace(/ {2}/g, ' ')
      .trim()
      .split(' ')
      .map((x) => {
        const k = keysNames.find((a) => a.key === x);
        return `${k !== undefined ? k.name : x}`;
      })
      .join(' ');
  }

  get userFriendlyBackendLogicalExpressionHtml(): string {
    const { audienceRules, logicalExpression } = AudienceSegmentUtils.audienceLogicFeToBe(this);

    return `${logicalExpression}`
      .replace(/&/g, ' AND ')
      .replace(/\|/g, ' OR ')
      .replace(/!/g, ' NOT ')
      .replace(/\(/g, ' ( ')
      .replace(/\)/g, ' ) ')
      .replace(/ {2}/g, ' ')
      .trim()
      .split(' ')
      .map((x) => {
        if (Number.isNaN(parseInt(x, 10))) return `<span>${x}</span>`;
        const rule = audienceRules.find((r) => r.key.toString() === x);
        const name = AudienceSegmentUtils.getSegmentNameForDemoRule(rule, audienceRules);
        return `<span class="rule">${name || x}</span>`;
      })
      .join(' ');
  }

  get endpoint(): string {
    return `/audiences/${this.id}`;
  }

  get createdOnFormatted(): string {
    return this.createdOnUtc != null
      ? DateTime.fromISO(this.createdOnUtc, { zone: 'utc' }).toLocal().toLocaleString(DateTime.DATETIME_MED)
      : DASH;
  }

  get modifiedOnFormatted(): string {
    return this.modifiedOnUtc != null
      ? DateTime.fromISO(this.modifiedOnUtc, { zone: 'utc' }).toLocal().toLocaleString(DateTime.DATETIME_MED)
      : DASH;
  }

  get hasMastercardRule(): boolean {
    return this.audienceRules.some((rule) => rule.mode === AudienceRuleModes.MastercardGeofence);
  }

  static validatorCreateRules(): { name: string; logicalExpression: string } {
    return {
      name: 'required|max:100|not_empty_or_whitespace',
      logicalExpression: 'required|max:1000|not_empty_or_whitespace',
    };
  }

  private static simplifyExpression(expression: string) {
    return `${expression}`
      .replace(/&/g, ' AND ')
      .replace(/\|/g, ' OR ')
      .replace(/!/g, ' NOT ')
      .replace(/\(/g, ' ( ')
      .replace(/\)/g, ' ) ')
      .replace(/ {2}/g, ' ')
      .trim();
  }
}
